import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store/configureStore';
import { notification, Modal, theme, Button } from 'antd';
import CustomTable from 'components/CustomTable/CustomTable';
import useFilters from 'utils/hooks/useFilters';
import { entityOp } from 'store/ducks/DataEntry/Entities';
import { IEntityTitle } from 'store/ducks/DataEntry/Entities/types';
import {
  set_addRowCount,
  set_isAddRow,
  set_isEntityAdded,
  set_nameOfEntity,
  set_idOfEntity,
  set_isSoftDeleted,
} from 'store/ducks/DataEntry/Entities/entitySlice';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import useCountries from 'utils/hooks/fetchHooks/useCountries';
import useEntityStatus from 'utils/hooks/useEntityStatus';
import { getEntityColumns } from 'components/Columns/EntityTableColumns';
import SaveButton from 'components/SaveButton/SaveButton';
import { globalOp } from 'store/ducks/Global';
import { requiredFieldsForEntity } from 'constants/requiredFields';
import { useTranslation } from 'react-i18next';
import * as Styled from '../../DataEntry.styled';
import { deleteRecord } from 'utils/hooks/deleteRecord';

const Entity: React.FC = () => {
  useEntityStatus();
  const { query } = useFilters();
  const { confirm } = Modal;
  const { useToken } = theme;
  const { token } = useToken();
  const { t } = useTranslation();

  const [dataSource, setDataSource] = useState<IEntityTitle[]>([]);
  const [initialData, setInitialData] = useState<IEntityTitle[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [entityTypes, setEntityTypes] = useState<
    { label: string; value: string }[]
  >([]);
  const [hierarchyInGroups, setHierarchyInGroups] = useState<
    { label: string; value: string }[]
  >([]);
  const [natures, setNatures] = useState<{ label: string; value: string }[]>(
    [],
  );
  const [competentAuthorities, setCompetentAuthorities] = useState<
    { label: string; value: string }[]
  >([]);
  const [currencies, setCurrencies] = useState<
    { label: string; value: string }[]
  >([]);
  const [entityMaintaining, setEntityMaintaining] = useState<any[]>([]);
  const [pageSize, setPageSize] = useState(10);
  const { countries } = useCountries();
  const {
    addRowCount,
    entities = [],
    isSoftDeleted,
  } = useSelector((state: RootState) => state.entities);
  const dispatch = useDispatch();
  const isLoggedIn = localStorage.getItem('authToken');

  const handlePageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  useEffect(() => {
    let combined: IEntityTitle[] = [];

    if (
      entities.length > 0 &&
      dataSource.length > 0 &&
      query.entity !== undefined
    ) {
      combined = [...entities, ...dataSource];
    } else if (entities.length < 1 && dataSource.length > 0) {
      combined = [...dataSource];
    } else if (dataSource.length < 1 && entities.length > 0) {
      combined = [...entities];
    }

    combined = combined.filter(
      (value, index, self) =>
        index === self.findIndex((t) => t.id === value.id),
    );
  }, [dataSource, entities, query.entity]);

  useEffect(() => {
    if (entities && entities.length > 0) {
      dispatch(set_addRowCount(1));
    }
  }, [entities]);

  const handleSave = async (row: IEntityTitle) => {
    setHasChanges(true);
    const isRowInDataSource = dataSource.some((item) => item.key === row.key);

    try {
      if (isRowInDataSource) {
        const updatedDataSource = dataSource.map((item) =>
          item.key === row.key ? { ...item, ...row } : item,
        );
        setDataSource(updatedDataSource);

        if (row.isNew) {
          dispatch(set_idOfEntity(row.id));
          dispatch(set_nameOfEntity(row.name_of_entity));
        }
      } else {
        const updatedEntities = entities.map((item) =>
          item.key === row.key ? { ...item, ...row } : item,
        );
      }
    } catch (error) {
      console.log('Save failed:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to save the row. Please try again.',
      });
    }
  };

  const fetchEntityType = async () => {
    try {
      const response = await entityOp.fetchEntityTypes();
      if (response?.data.entity_types) {
        const formattedOptions = response.data.entity_types.map(
          (reasons: { name: any; id: any }) => ({
            label: reasons.name,
            value: reasons.id,
          }),
        );
        setEntityTypes(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchEntityMaintaining = async () => {
    try {
      const response = await entityOp.fetchAllFinancialEntities();
      const data = response?.data;
      if (data.length > 0) {
        const updatedTPPData = data.map((item: any, index: number) => ({
          key: index,
          id: item.id,
          financial_entity_name: item.financial_entity_name,
        }));
        setEntityMaintaining(updatedTPPData);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchHierarchyInGroups = async () => {
    try {
      const response = await entityOp.fetchHierarchyInGroup();
      if (response?.data.hierarchy_groups) {
        const formattedOptions = response.data.hierarchy_groups.map(
          (reasons: { name: any; id: any }) => ({
            label: reasons.name,
            value: reasons.id,
          }),
        );
        setHierarchyInGroups(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchNatures = async () => {
    try {
      const response = await entityOp.fetchNatureID();
      if (response?.data.nature_of_entities) {
        const formattedOptions = response.data.nature_of_entities.map(
          (reasons: { name: any; id: any }) => ({
            label: reasons.name,
            value: reasons.id,
          }),
        );
        setNatures(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchCompetentAuthorities = async () => {
    try {
      const response = await entityOp.fetchCompetentAuthorities();
      if (response?.data.competent_authorities) {
        const formattedOptions = response.data.competent_authorities.map(
          (reasons: { name: any; id: any }) => ({
            label: reasons.name,
            value: reasons.id,
          }),
        );
        setCompetentAuthorities(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await entityOp.fetchCurrencies();
      if (response?.data.currencies) {
        const formattedOptions = response.data.currencies.map(
          (reasons: { currency_name: any; id: any }) => ({
            label: reasons.currency_name,
            value: reasons.id,
          }),
        );
        setCurrencies(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleAddRow = (record?: any, index?: number) => {
    setHasChanges(true);
    dispatch(set_isAddRow(true));

    const newData: IEntityTitle = {
      key: generateUniqueId(),
      id: generateUniqueId(),
      lei_entity_maintaining_register_information: '',
      name_of_entity: '',
      entity_type_id: '',
      lei_of_entity: '',
      hierarchy_in_group_id: '',
      parent_lei: '',
      lei_entity_making_use_ICT_service: '',
      lei_entity_signing_contractual_arrangement: '',
      nature_id: '',
      integration_date: '',
      deletion_date: '9999-01-01',
      total_assets: 0,
      description: '',
      competent_authority_id: '',
      country_id: '',
      date_of_last_update: '',
      end_date: '9999-01-01',
      financial_entity_lei_id: '',
      reporting_date: '',
      start_date: '',
      currency_id: '',
      name_of_financial_entity: '',
      isNew: true,
    };
    const savedDataSource =
      entities.length > 0 ? [...entities] : [...dataSource];

    let updatedDataSource = [...savedDataSource];
    if (record) {
      const insertIndex = updatedDataSource.findIndex(
        (item) => item.key === record.key,
      );
      updatedDataSource = [
        ...updatedDataSource.slice(0, insertIndex + 1),
        newData,
        ...updatedDataSource.slice(insertIndex + 1),
      ];
    } else {
      updatedDataSource.push(newData);
    }

    setDataSource(updatedDataSource);
  };

  const fetchEntity = async () => {
    try {
      const response = await entityOp.fetchEntitiesByEntityName({
        entity_id: query.entityId,
        entity_name: query.entity,
      });
      const data = response?.data;
      const mappedData = [
        {
          key: data.entity_id,
          id: data.entity_id,
          active: data.active,
          lei_entity_maintaining_register_information:
            data.lei_entity_maintaining_register_information || '',
          name_of_entity: data.entity_name || '',
          entity_type_id: data.entity_type_id || undefined,
          lei_of_entity: data.lei_of_entity || '',
          hierarchy_in_group_id: data.hierarchy_in_group_id || undefined,
          parent_lei: data.parent_lei || '',
          lei_entity_making_use_ICT_service:
            data.lei_entity_making_use_ICT_service || '',
          lei_entity_signing_contractual_arrangement:
            data.lei_entity_signing_contractual_arrangement || '',
          nature_id: data.nature_id || undefined,
          integration_date: data.integration_date || '',
          deletion_date: data.deletion_date || '',
          total_assets: data.total_assets || 0,
          description: data.description || '',
          competent_authority_id: data.competent_authority_id || undefined,
          country_id: data.country_id || undefined,
          date_of_last_update: data.date_of_last_update || '',
          end_date: data.end_date || '',
          financial_entity_lei_id: data.financial_entity_lei_id || '',
          reporting_date: data.reporting_date || '',
          start_date: data.start_date || '',
          currency_id: data.currency_id || undefined,
          name_of_financial_entity: data.name_of_financial_entity || '',
          isNew: false,
          isEdited: false,
        },
      ];
      setDataSource(mappedData);
      setInitialData(mappedData);
    } catch (error) {
      console.log(error, 'error');
    }
  };

  useEffect(() => {
    if (query.entity) {
      fetchEntity();
      fetchEntityType();
      fetchHierarchyInGroups();
      fetchEntityMaintaining();
      fetchNatures();
      fetchCompetentAuthorities();
      fetchCurrencies();
    } else {
      if (entities) {
        fetchEntityType();
        fetchHierarchyInGroups();
        fetchEntityMaintaining();
        fetchNatures();
        fetchCompetentAuthorities();
        fetchCurrencies();
        dispatch(set_addRowCount(0));
      }
    }
  }, [query]);

  useEffect(() => {
    if (isSoftDeleted) {
      setDataSource([]);
      setHasChanges(false);
    }
  }, [isSoftDeleted]);

  const areAllFieldsFilled = (entities: IEntityTitle[]) => {
    return entities.every((item) => {
      const invalidKeys = Object.entries(item).filter(([key, value]) => {
        if (key === 'description') return false;
        return value === '' || value === null || value === undefined;
      });
      return invalidKeys.length === 0;
    });
  };

  const handleRemoveRow = (key: number) => {
    Modal.confirm({
      title: 'Are you sure you want to remove this row?',
      content:
        'Once deleted, this record cannot be restored. Would you like to continue?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        const updatedDataSource = dataSource.filter((item) => item.key !== key);
        setDataSource(updatedDataSource);
        dispatch(set_addRowCount(0));

        notification.success({
          message: 'Info',
          description: 'The row has been successfully removed.',
          duration: 10,
        });
      },
      onCancel: () => {
        notification.info({
          message: 'Info',
          description: 'The row removal has been canceled.',
          duration: 10,
        });
      },
    });
  };

  const handleInactivate = (record: IEntityTitle) => {
    if (!record.id) {
      notification.error({
        message: 'Info',
        description: 'Id is missing.',
      });
      return;
    }

    deleteRecord({
      id: record.id,
      tableName: 'entity',
      onSuccess: () => {
        fetchEntity();
        dispatch(set_isSoftDeleted(true));
      },
    });
  };

  useEffect(() => {
    if (addRowCount === 1) {
      const allFieldsFilled = areAllFieldsFilled(entities);
      if (allFieldsFilled) {
        dispatch(set_isEntityAdded(true));
      } else {
        dispatch(set_isEntityAdded(false));
      }
    }
  }, [entities, addRowCount]);

  const handleSubmitEntity = async () => {
    const updatedData = dataSource.map((item) => ({
      ...item,
      total_assets: Number(item.total_assets),
    }));
    if (JSON.stringify(updatedData) === JSON.stringify(initialData)) {
      notification.info({
        message: 'Info',
        description: 'No data has been changed.',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }

    const hasEmptyFields = updatedData.some((item) =>
      (requiredFieldsForEntity as (keyof typeof item)[]).some(
        (field) => item[field] === '' || item[field] === null,
      ),
    );

    if (hasEmptyFields) {
      notification.error({
        message: '',
        description:
          'Please complete all the fields highlighted in yellow and make sure to enter a start date before submitting.',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }

    if (entities.length !== 0) {
      const allFieldsFilled = areAllFieldsFilled(entities);
      if (!allFieldsFilled) {
      } else {
        dispatch(set_isEntityAdded(true));
      }
    } else {
      //  setCanAddRow(true);
    }
    try {
      const leis = updatedData
        .flatMap((item) => [
          item.lei_entity_maintaining_register_information,
          item.lei_of_entity,
          item.lei_entity_making_use_ICT_service,
          item.lei_entity_signing_contractual_arrangement,
        ])
        .filter(Boolean);

      const validationResults = await globalOp.validateLEI(leis);

      Object.entries(validationResults).forEach(
        ([lei, result]: [string, any]) => {
          const notificationConfig = {
            message: lei,
            description: result.details,
            duration: 5,
          };

          if (result.valid) {
            notification.success(notificationConfig);
          } else {
            notification.error(notificationConfig);
          }
        },
      );

      const allValid = Object.values(validationResults).map(
        (r: any) => r.valid === true,
      );

      if (allValid.every(Boolean)) {
        const response = await entityOp.fetchEntity(
          [...updatedData],
          isLoggedIn ?? '',
        );
        const resetData = dataSource.map((item) => ({
          ...item,
          isNew: false,
          isEdited: false,
        }));
        setDataSource(resetData);
        setInitialData(resetData);
        setHasChanges(false);
        dispatch(globalOp.setFilters() as any);
      } else {
        console.warn('One or more LEIs are invalid');
      }
    } catch (error) {
      notification.error({
        message: 'Info',
        description: 'There was an error sending the data. Please try again.',
        placement: 'topRight',
        duration: 20,
      });
    }
  };

  const columns = getEntityColumns(
    handleSave,
    handleRemoveRow,
    handleInactivate,
    handleAddRow,
    countries,
    entityTypes,
    hierarchyInGroups,
    competentAuthorities,
    currencies,
    natures,
    entityMaintaining,
    t,
  );

  return (
    dataSource && (
      <div>
        <Styled.HighlightedTable>
          <CustomTable
            columns={columns}
            dataSource={dataSource}
            handleSave={handleSave}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              onChange: handlePageChange,
              showSizeChanger: true,
            }}
            locale={{
              emptyText: (
                <Button type="primary" onClick={() => handleAddRow()}>
                  {t('GENERAL.ADD_FIRST_ROW')}
                </Button>
              ),
            }}
          />
        </Styled.HighlightedTable>
        <Styled.ButtonRow>
          {dataSource.length > 0 && (
            <Styled.AreaButton
              token={token}
              type="primary"
              onClick={() => handleAddRow()}
            >
              Add new row
            </Styled.AreaButton>
          )}
          <SaveButton
            token={token}
            hasChanges={hasChanges}
            onClick={handleSubmitEntity}
          />
        </Styled.ButtonRow>
      </div>
    )
  );
};

export default Entity;
